import React, { useEffect, useRef } from 'react';
import { 
    Bold, Italic, Underline, List as ListIcon, 
    Link as LinkIcon, RemoveFormatting, Undo, Redo,
    Strikethrough, Heading1, Heading2, Quote,
    AlignLeft, AlignCenter, AlignRight
} from 'lucide-react';

const RichTextEditor = ({ value, onChange }) => {
    const editorRef = useRef(null);

    const execCmd = (cmd, arg = null) => { 
        document.execCommand(cmd, false, arg); 
        editorRef.current.focus(); 
    };

    const handleInput = () => { 
        if (editorRef.current) onChange(editorRef.current.innerHTML); 
    };

    useEffect(() => { 
        if (editorRef.current && value !== editorRef.current.innerHTML) {
            editorRef.current.innerHTML = value; 
        }
    }, [value]);

    return (
        <div className="flex flex-col border border-slate-300 rounded-lg overflow-hidden bg-white shadow-sm focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent transition-all h-full">
            <div className="flex flex-wrap items-center gap-1 p-2 border-b border-slate-200 bg-slate-50 text-slate-600">
                {/* History */}
                <div className="flex gap-0.5 pr-1 mr-1 border-r border-slate-200">
                    <button type="button" onClick={() => execCmd('undo')} className="p-1.5 hover:bg-slate-200 rounded" title="Undo"><Undo className="w-4 h-4" /></button>
                    <button type="button" onClick={() => execCmd('redo')} className="p-1.5 hover:bg-slate-200 rounded" title="Redo"><Redo className="w-4 h-4" /></button>
                </div>

                {/* Text Style */}
                <div className="flex gap-0.5 pr-1 mr-1 border-r border-slate-200">
                    <button type="button" onClick={() => execCmd('bold')} className="p-1.5 hover:bg-slate-200 rounded" title="Bold"><Bold className="w-4 h-4" /></button>
                    <button type="button" onClick={() => execCmd('italic')} className="p-1.5 hover:bg-slate-200 rounded" title="Italic"><Italic className="w-4 h-4" /></button>
                    <button type="button" onClick={() => execCmd('underline')} className="p-1.5 hover:bg-slate-200 rounded" title="Underline"><Underline className="w-4 h-4" /></button>
                    <button type="button" onClick={() => execCmd('strikethrough')} className="p-1.5 hover:bg-slate-200 rounded" title="Strikethrough"><Strikethrough className="w-4 h-4" /></button>
                </div>

                {/* Headings & Quote */}
                <div className="flex gap-0.5 pr-1 mr-1 border-r border-slate-200">
                    <button type="button" onClick={() => execCmd('formatBlock', 'H1')} className="p-1.5 hover:bg-slate-200 rounded" title="Heading 1"><Heading1 className="w-4 h-4" /></button>
                    <button type="button" onClick={() => execCmd('formatBlock', 'H2')} className="p-1.5 hover:bg-slate-200 rounded" title="Heading 2"><Heading2 className="w-4 h-4" /></button>
                    <button type="button" onClick={() => execCmd('formatBlock', 'blockquote')} className="p-1.5 hover:bg-slate-200 rounded" title="Quote"><Quote className="w-4 h-4" /></button>
                </div>

                {/* Alignment */}
                <div className="flex gap-0.5 pr-1 mr-1 border-r border-slate-200">
                    <button type="button" onClick={() => execCmd('justifyLeft')} className="p-1.5 hover:bg-slate-200 rounded" title="Align Left"><AlignLeft className="w-4 h-4" /></button>
                    <button type="button" onClick={() => execCmd('justifyCenter')} className="p-1.5 hover:bg-slate-200 rounded" title="Align Center"><AlignCenter className="w-4 h-4" /></button>
                    <button type="button" onClick={() => execCmd('justifyRight')} className="p-1.5 hover:bg-slate-200 rounded" title="Align Right"><AlignRight className="w-4 h-4" /></button>
                </div>

                {/* List & Link */}
                <div className="flex gap-0.5 pr-1 mr-1 border-r border-slate-200">
                    <button type="button" onClick={() => execCmd('insertUnorderedList')} className="p-1.5 hover:bg-slate-200 rounded" title="Bullet List"><ListIcon className="w-4 h-4" /></button>
                    <button type="button" onClick={() => {
                        const url = prompt('Enter link URL:');
                        if (url) execCmd('createLink', url);
                    }} className="p-1.5 hover:bg-slate-200 rounded" title="Insert Link"><LinkIcon className="w-4 h-4" /></button>
                </div>

                <div className="ml-auto">
                    <button type="button" onClick={() => execCmd('removeFormat')} className="p-1.5 hover:bg-red-100 text-slate-500 hover:text-red-600 rounded" title="Clear Format"><RemoveFormatting className="w-4 h-4" /></button>
                </div>
            </div>
            <div 
                ref={editorRef} 
                contentEditable 
                className="flex-1 p-4 min-h-[200px] outline-none text-sm text-slate-800 overflow-y-auto" 
                onInput={handleInput} 
                suppressContentEditableWarning={true}
                spellCheck={true}
            />
        </div>
    );
};

export default RichTextEditor;